import { ApplicationId } from '@nx-customer-apps/components';
import {
    ApplicationRoles,
    CountryCode,
    ExternalTool,
    RemoteConfigParameter,
    ReportConfigurationSection
} from '@nx-customer-apps/shared/enums';

export const environment = {
    production: false,
    validateAccess: false,
    applicationName: 'ViGuide Planning',
    baseUrl: 'https://viguide-planning-integration.viessmann-climatesolutions.com',
    baseApiUrl: 'https://api-integration.viessmann-climatesolutions.com',
    environmentHeaders: {},
    externalTools: {
        [ExternalTool.HeatPumpPlanner]: 'http://viguide-planning-hp-integration.viessmann-climatesolutions.com',
        [ExternalTool.PhotovoltaicPlanner]: 'https://viguide-planning-pv-integration.viessmann-climatesolutions.com',
        [ExternalTool.QuoteAssist]: 'https://qa-integration.viessmann-climatesolutions.com',
        [ExternalTool.PartnerShop]: 'https://shop-test.viessmann-climatesolutions.com',
        [ExternalTool.ViGuide]: 'https://viguide-staging.viessmann-climatesolutions.com',
        [ExternalTool.VitoAirPlanner]: 'https://vitoair-planner-integration.viessmann-climatesolutions.com'
    },
    serviceWorker: true,
    environment: 'integration',
    hotJarEnabled: true,
    partnerPortalBaseUrl: 'https://partnerportal-staging.viessmann-climatesolutions.com',
    everestApiUrl: 'https://api-integration.viessmann-climatesolutions.com',
    accountApiUrl: 'https://account-integration.viessmann-climatesolutions.com',
    registerRedirectUrl: 'https://viguide-planning-integration.viessmann-climatesolutions.com',
    localeConfig: {
        localStorageKey: 'locale'
    },
    storageKeys: {
        locale: 'locale',
        nextRoute: 'nextRoute',
        lockProjectId: 'lockProjectId',
        onboarding: 'onboarding'
    },
    projectLockConfig: {
        nextAttemptLockDelay: 5000, // 5 seconds
        verifyLockPollingInterval: 1000 * 60 * 10, // 1 minute,
        disabled: true
    },
    csrfTokenConfig: {
        appId: 'viguide-planning-projects',
        localStorageKey: 'csrf_token',
        apiUrl: 'https://api-integration.viessmann-climatesolutions.com/auth/v1/saml/csrf'
    },
    meUrl: 'https://api-integration.viessmann-climatesolutions.com/users/v1/users/me',
    samlConfig: {
        requestLoginUrl: 'https://api-integration.viessmann-climatesolutions.com/saml/sso/request'
    },
    globalHeader: {
        iamBackgroundImage: 'iam_lpi_000001.png'
    },
    remoteConfig: {
        remoteConfigUrl: 'https://api-integration.viessmann-climatesolutions.com/remote-config/v3/fetch',
        appId: 'dccdfb2f-3701-4c76-92a2-6b6b69ab1fc1',
        enabled: true,
        defaultConfig: {
            [RemoteConfigParameter.CountryDropdownByCountry]: `${CountryCode.DE},${CountryCode.NL},${CountryCode.AT},${CountryCode.CH},${CountryCode.FR},${CountryCode.BE},${CountryCode.CZ},${CountryCode.SK},${CountryCode.PL},${CountryCode.ES},${CountryCode.LU}`,
            [RemoteConfigParameter.CountryLanguageSwitcherByCountry]: `${CountryCode.DE},${CountryCode.AT},${CountryCode.CH},${CountryCode.LU},${CountryCode.FR},${CountryCode.BE},${CountryCode.CZ},${CountryCode.SK},${CountryCode.PL},${CountryCode.ES}`,
            [RemoteConfigParameter.QuoteAssistByCountry]: `${CountryCode.DE},${CountryCode.AT},${CountryCode.CH},${CountryCode.FR},${CountryCode.BE},${CountryCode.CZ},${CountryCode.SK},${CountryCode.ES}`,
            [RemoteConfigParameter.PartnerShopByCountry]: `${CountryCode.DE},${CountryCode.AT},${CountryCode.CH},${CountryCode.FR},${CountryCode.BE},${CountryCode.CZ},${CountryCode.SK},${CountryCode.ES}`,
            [RemoteConfigParameter.DeviceCalculationByCountry]: `${CountryCode.DE},${CountryCode.AT},${CountryCode.CH},${CountryCode.FR},${CountryCode.BE},${CountryCode.CZ},${CountryCode.SK},${CountryCode.ES}`,
            [RemoteConfigParameter.DeviceCalculationByRole]: ApplicationRoles.HeatLoadByIOT,
            [RemoteConfigParameter.PdfConfiguratorBySection]: `${ReportConfigurationSection.GeneralInformation},${ReportConfigurationSection.HeatPumpPlan},${ReportConfigurationSection.PVPlan},${ReportConfigurationSection.VentilationPlan},${ReportConfigurationSection.FullSystem},${ReportConfigurationSection.LegalNotice}`,
            [RemoteConfigParameter.PdfConfiguratorGeneralInformationBySubsection]: `${ReportConfigurationSection.GeneralProjectData},${ReportConfigurationSection.CurrentSystem},${ReportConfigurationSection.PlannedSystem},${ReportConfigurationSection.EnergyPrices},${ReportConfigurationSection.HeatLoadCalculations},${ReportConfigurationSection.GEGRecommendations}`,
            [RemoteConfigParameter.PdfConfiguratorHeatPumpPlanBySubsection]: `${ReportConfigurationSection.HPSimulationResults},${ReportConfigurationSection.HPFinancialPerformance},${ReportConfigurationSection.AppendixJAZ},${ReportConfigurationSection.HPBillOfMaterials}`,
            [RemoteConfigParameter.PdfConfiguratorPhotovoltaicPlanBySubsection]: `${ReportConfigurationSection.Roofs},${ReportConfigurationSection.PVTechnicalDetails},${ReportConfigurationSection.PVSimulationResults},${ReportConfigurationSection.PVInstallationPlans},${ReportConfigurationSection.PVStaticsAnalysisReport},${ReportConfigurationSection.PVFinancialPerformance},${ReportConfigurationSection.PVBillOfMaterials}`,
            [RemoteConfigParameter.PdfConfiguratorVentilationPlanBySubsection]: ',',
            [RemoteConfigParameter.PdfConfiguratorFullSystemBySubsection]: `${ReportConfigurationSection.FullSystemSimulation},${ReportConfigurationSection.FinancialPerformance},${ReportConfigurationSection.BillOfMaterials}`,
            [RemoteConfigParameter.PdfConfiguratorLegalNoticeBySubsection]: ',',
            [RemoteConfigParameter.DomesticElectricityDayNightTariffByCountry]: `${CountryCode.DE},${CountryCode.AT},${CountryCode.BE},${CountryCode.CH},${CountryCode.PL},${CountryCode.FR}`,
            [RemoteConfigParameter.HeatPumpElectricityFlatRateByCountry]: `${CountryCode.DE},${CountryCode.AT},${CountryCode.CH},${CountryCode.PL}`,
            [RemoteConfigParameter.HeatPumpElectricityDayNightTariffByCountry]: `${CountryCode.DE},${CountryCode.AT},${CountryCode.CH},${CountryCode.PL}`,
            [RemoteConfigParameter.HeatPumpElectricityBlockTimeByCountry]: `${CountryCode.AT},${CountryCode.CH}`,
            [RemoteConfigParameter.HeatPumpElectricityBlockTimeDisabledByCountry]: `${CountryCode.AT},${CountryCode.CH}`,
            [RemoteConfigParameter.PdfConfiguratorPitchDeckByLocale]: 'en-DE,de-DE,de-AT,de-CH',
            [RemoteConfigParameter.PdfConfiguratorDocumentationTabVisible]: false,
            [RemoteConfigParameter.BoilerPlannerAvailable]: true,
            [RemoteConfigParameter.OnboardingVersion]: '2.22.0',
            [RemoteConfigParameter.IncludeArchivedProjects]: false,
            [RemoteConfigParameter.MaintenanceAllowedRoles]: `${ApplicationRoles.Developer},${ApplicationRoles.TestAccount}`,
            [RemoteConfigParameter.IsVitoAirAvailable]: true,
            [RemoteConfigParameter.BasicProfitabilityByCountry]: `${CountryCode.DE}`,
            [RemoteConfigParameter.IsProfitabilityAvailable]: true,
            [RemoteConfigParameter.VitoAirAvailableByLocale]: 'de-DE,en-DE,de-CH,fr-CH,de-AT,fr-BE,nl-BE,es-ES,sk-SK,cs-CZ,pl-PL',
            [RemoteConfigParameter.HasUserVariantOptions]: false,
            [RemoteConfigParameter.HasUserVariantPlanningOptions]: true,
            [RemoteConfigParameter.IsPitchDeckAvailableForAllVariants]: true,
            [RemoteConfigParameter.IsWelcomeBannerAvailable]: true
        }
    },
    statusPage: {
        apiUrl: 'https://api-integration.viessmann-climatesolutions.com/status/v1/apps/slt2bwjg6sd2/summary',
        pollingInterval: 10 * 60 * 1000 // every 10 minutes
    },
    apiPlanningProjectsConfig: {
        apiBaseUrl: 'https://api-integration.viessmann-climatesolutions.com/planning-projects/v1',
        applicationId: ApplicationId.ViGuidePlanning
    }
};
